import { useCallback, useMemo, useRef } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { FindingsBulkFilter } from '../../clients/testerClient';
import { terraProxyApiClient } from '../../clients/testerClient';
import type {
  FindingSeverityType,
  FindingStatusType,
  SignalCategoryType,
  TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import type { FindingUpdateBodyDto } from '@terra/shared-contracts-findings';
import type { FindingsSortBy } from './findings-model';
import {
  countFindingsForTarget,
  fetchFindingsListForTarget,
} from './target-findings-api';
import { useInvalidateFindingById } from './useFindingById';
import { invalidateSignalQueriesAfterFindingStatusChange } from './invalidateSignalQueriesAfterFindingStatusChange';
import {
  buildFindingsHeaderTotalFilter,
  needsUnfilteredFindingsTotal,
} from '../../utils/needs-unfiltered-findings-total';
import {
  FINDINGS_COUNT_QUERY_KEY,
  getFindingsHeaderTotalCountQueryKey,
  getFindingsMatchingCountQueryKey,
} from './findings-matching-count-query';

export {
  toInternalEnrichedFinding,
  toInternalEnrichedFindingFromSurface,
  type FindingsSortBy,
} from './findings-model';
export { fetchFindingsPageForTarget } from './target-findings-api';

export const FINDINGS_QUERY_KEY = 'findings-by-application-and-endpoint';
export const useInvalidateFindingsCounts = () => {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [FINDINGS_COUNT_QUERY_KEY],
      exact: false,
    });
  }, [queryClient]);
};

export const useFindingsCounts = ({
  targetId,
  targetType,
  filter,
  enabled = true,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  filter: FindingsBulkFilter;
  enabled?: boolean;
}) => {
  const invalidateCounts = useInvalidateFindingsCounts();
  const fetchHeaderTotal = needsUnfilteredFindingsTotal(filter);
  const headerTotalFilter = useMemo(
    () => buildFindingsHeaderTotalFilter({ endpointId: filter.endpointId }),
    [filter.endpointId],
  );
  const lastMatchingCountRef = useRef<number | null>(null);
  const lastHeaderTotalCountRef = useRef<number | null>(null);
  const targetScopeKey = `${targetId ?? ''}|${targetType}`;
  const previousTargetScopeKeyRef = useRef(targetScopeKey);

  if (previousTargetScopeKeyRef.current !== targetScopeKey) {
    previousTargetScopeKeyRef.current = targetScopeKey;
    lastMatchingCountRef.current = null;
    lastHeaderTotalCountRef.current = null;
  }

  const matchingQuery = useQuery({
    queryKey: getFindingsMatchingCountQueryKey({
      targetId: targetId ?? '',
      targetType,
      filter,
    }),
    queryFn: () => {
      if (!targetId) {
        throw new Error('targetId is required');
      }

      return countFindingsForTarget({
        targetId,
        targetType,
        filter,
      });
    },
    enabled: !!targetId && enabled,
    placeholderData: keepPreviousData,
  });

  const totalQuery = useQuery({
    queryKey: getFindingsHeaderTotalCountQueryKey({
      targetId: targetId ?? '',
      targetType,
      filter: headerTotalFilter,
    }),
    queryFn: () => {
      if (!targetId) {
        throw new Error('targetId is required');
      }

      return countFindingsForTarget({
        targetId,
        targetType,
        filter: headerTotalFilter,
      });
    },
    enabled: !!targetId && enabled && fetchHeaderTotal,
    placeholderData: keepPreviousData,
  });

  if (matchingQuery.data !== undefined) {
    lastMatchingCountRef.current = matchingQuery.data;
  }

  if (totalQuery.data !== undefined) {
    lastHeaderTotalCountRef.current = totalQuery.data;
  }

  const matchingCount =
    matchingQuery.data ?? lastMatchingCountRef.current ?? null;
  const totalCount = fetchHeaderTotal
    ? (totalQuery.data ?? lastHeaderTotalCountRef.current ?? null)
    : null;

  return {
    matchingCount,
    totalCount,
    invalidateCounts,
  };
};

export const useFindingsSelectionCount = ({
  targetId,
  targetType,
  filter,
  excludedIds,
  enabled = true,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  filter: FindingsBulkFilter;
  excludedIds: string[];
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      FINDINGS_COUNT_QUERY_KEY,
      'selection',
      targetId,
      targetType,
      filter,
      excludedIds,
    ],
    queryFn: () => {
      if (!targetId) {
        throw new Error('targetId is required');
      }

      return countFindingsForTarget({
        targetId,
        targetType,
        filter,
        excludedIds,
      });
    },
    enabled: !!targetId && enabled,
    placeholderData: keepPreviousData,
  });
};

export { useInvalidateFindingById };

type UseFindingsFilters = {
  searchTerm?: string;
  severity?: FindingSeverityType[];
  status?: FindingStatusType[];
  category?: SignalCategoryType[];
  isPublished?: boolean;
  hasPrevention?: boolean;
  createdStartDate?: Date | null;
  createdEndDate?: Date | null;
  sortBy?: FindingsSortBy;
  sortOrder?: 'asc' | 'desc';
};

export const useInvalidateFindings = () => {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [FINDINGS_QUERY_KEY],
      exact: false,
    });
  }, [queryClient]);
};

type UpdateFindingParams = {
  findingId: string;
  patch: Pick<
    FindingUpdateBodyDto,
    'status' | 'isPublished' | 'assignedTo' | 'note' | 'disproveRelatedSignal'
  >;
  signalId?: string | null;
};

export const useUpdateFinding = () => {
  const queryClient = useQueryClient();
  const invalidateFindings = useInvalidateFindings();
  const invalidateFindingById = useInvalidateFindingById();

  const mutation = useMutation({
    mutationFn: async ({ findingId, patch }: UpdateFindingParams) =>
      terraProxyApiClient.updateFinding(findingId, patch),
    onSuccess: async (response, { findingId, patch, signalId }) => {
      if (response.status !== 200) {
        console.warn('useUpdateFinding: skipping cache invalidation', {
          status: response.status,
          findingId,
        });

        return;
      }

      await invalidateFindingById(findingId);
      await invalidateFindings();

      if (patch.status) {
        invalidateSignalQueriesAfterFindingStatusChange({
          queryClient,
          signalId,
        });
      }
    },
  });

  const updateFinding = useCallback(
    (params: UpdateFindingParams) => mutation.mutateAsync(params),
    [mutation],
  );

  return {
    updateFinding,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
};

export const useFindings = ({
  targetId,
  targetType,
  endpointId,
  filters,
  pagination,
  enabled = true,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  endpointId?: string;
  filters?: UseFindingsFilters;
  pagination?: {
    skip?: number;
    limit?: number;
  };
  enabled?: boolean;
}) => {
  const invalidateFindings = useInvalidateFindings();

  const result = useQuery({
    queryKey: [
      FINDINGS_QUERY_KEY,
      targetId,
      targetType,
      endpointId,
      filters,
      pagination,
    ],
    queryFn: async () => {
      if (!targetId) {
        return [];
      }

      return fetchFindingsListForTarget({
        targetId,
        targetType,
        endpointId,
        options: {
          skip: pagination?.skip,
          limit: pagination?.limit,
          searchTerm: filters?.searchTerm?.trim() || undefined,
          severity: filters?.severity?.length ? filters.severity : undefined,
          status: filters?.status?.length ? filters.status : undefined,
          category: filters?.category?.length ? filters.category : undefined,
          isPublished: filters?.isPublished,
          hasPrevention: filters?.hasPrevention,
          createdStartDate: filters?.createdStartDate ?? undefined,
          createdEndDate: filters?.createdEndDate ?? undefined,
          sortBy: filters?.sortBy,
          sortOrder: filters?.sortOrder,
        },
      });
    },
    enabled: !!targetId && enabled,
    placeholderData: keepPreviousData,
    gcTime: 900000,
  });

  const findings = useMemo(() => result.data ?? [], [result.data]);

  return {
    findings,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isFetched: result.isFetched,
    error: result.error,
    refetch: result.refetch,
    invalidate: invalidateFindings,
  };
};
